/**
 * Base abstract builder class that all other builders extend
 */
import { Builder, ConditionalBuilder } from '../types';
import { deepClone } from '../utils';

/**
 * Abstract base builder class with common functionality
 */
export abstract class BaseBuilder<T, B extends Builder<T>> implements Builder<T>, ConditionalBuilder<B> {
  protected data: Partial<T>;
  
  /**
   * Creates a new builder instance
   */
  constructor(initialData: Partial<T> = {}) {
    this.data = initialData;
  }
  
  /**
   * Builds and returns the final object
   */
  abstract build(): T;
  
  /**
   * Converts the builder to a JSON string
   */
  toJSON(): string {
    return JSON.stringify(this.build());
  }
  
  /**
   * Conditionally executes the provided function if the condition is true
   * 
   * @param condition - Boolean condition to evaluate
   * @param fn - Function to execute if condition is true
   */
  when(condition: boolean, fn: (builder: B) => B): B {
    if (condition) {
      return fn(this as unknown as B);
    }
    return this as unknown as B;
  }
  
  /**
   * Conditionally executes the provided function if the condition is false
   * 
   * @param condition - Boolean condition to evaluate
   * @param fn - Function to execute if condition is false
   */
  unless(condition: boolean, fn: (builder: B) => B): B {
    return this.when(!condition, fn);
  }
  
  /**
   * Creates a copy of the current builder
   */
  protected copy(): this {
    const copyInstance = this.constructor as new (data: Partial<T>) => this;
    return new copyInstance(deepClone(this.data));
  }
}
